import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { ProgressSpinner } from "primereact/progressspinner";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { TabView, TabPanel } from "primereact/tabview";
import {
  BrokerCommissionInput,
  FormSelectionDropdown,
  PerilCheckboxes,
} from "../policy-terms/PolicyTermControls";
import { confirmDialog } from "primereact/confirmdialog";
import styles from "./LayersCard.module.css";
import { RunTerm, showTermColumns } from "./termColumns";
import {
  PerilDataSource,
  readableLayer,
  participationPercent,
  formatParticipationPercent,
  readableLimitOfLiabilityFromLayer,
  sortedLayersFromReport,
  PERIL_TYPE_ORDER,
  perilCodeDisplayName,
  MIN_MULTIPLE,
  isModeledCatPeril,
  aalRollup,
  aalMultiple,
  sumModeledAal,
} from "./util";
import { BulkPerilOverrideDialog } from "./BulkPerilOverrideDialog";
import { GateExposureDialog } from "./GateExposureDialog";
import { PmiDialog } from "./PmiDialog";
import { FinancialModelAuditDialog } from "./FinancialModelAuditDialog";
import {
  DisplayPerilDataSource,
  SOURCE_LABELS,
  SourceBadge,
} from "./SourceBadge";
import { IntegrationStatusMessage } from "./IntegrationStatusMessage";
import { rmsSourceLabel } from "../cat/RmsVersionBadge";

import { SafeReportResponse } from "../Conversion";
import { formatTwoDecimals } from "../utils";
import * as React from "react";
import { useState, useMemo, useContext, useRef } from "react";
import { ThemeContext } from "../../../themes/Theme";
import {
  HIGHLIGHT_WARNING_LIGHT_MODE,
  HIGHLIGHT_WARNING_DARK_MODE,
  HIGHLIGHT_ERROR_LIGHT_MODE,
  HIGHLIGHT_ERROR_DARK_MODE,
  HIGHLIGHT_USER_OVERRIDE_LIGHT_MODE,
  HIGHLIGHT_USER_OVERRIDE_DARK_MODE,
} from "../dashboard/util";

import { cloneDeep } from "lodash";
import * as client from "../../../client";
import { TooltipWrapper } from "../../../components/tooltip/TooltipWrapper";
import {
  DBPerilType,
  perilSentenceFromEnabledPerils,
  readableNameFromPeril,
} from "../general-input/util";
import {
  useCalculatedPremium,
  useCatVendorEnrichment,
  useLocalUser,
  useSelectedPerilSources,
  useUpdateReport,
} from "../data";
import { EnrichmentBadge } from "../cat/EnrichmentBadge";
import { ENRICHMENT_VENDOR_LABELS } from "../cat/types";
import { VendorEnrichmentDialog } from "./VendorEnrichmentDialog";
import {
  enrichmentVendorsForSource,
  liveSourcesByVendor,
} from "./enrichmentColumn";
import { useFeatureEnabled } from "../../../IfFeatureEnabled";
import { v4 as uuid } from "uuid";
import { formatExactMoneyDollars } from "../Money";
import {
  WarningLevel,
  warningSeverity,
  WARNING_DISPLAY_NAMES,
} from "../rater/warnings";

const maxWarningLevel = (keys: string[]): WarningLevel | undefined => {
  if (keys.some((k) => warningSeverity(k) === "critical")) return "critical";
  return keys.length > 0 ? "info" : undefined;
};

type LayersCardProps = {
  specialtyPropertyInfo: SafeReportResponse;
  title?: string;
};

type SourcePremiumData = {
  /** The modeled loss the premium is priced on: the policy term's loss (TAL) when
   * `runTerm` is "policy", otherwise the annual loss. */
  aal: number | null;
  /** The annual loss beside a policy-term `aal`; null when `aal` is already
   * annual or the source's annual companion run has not completed. */
  annualAal: number | null;
  /** The term the source's run priced, from its run identity. */
  runTerm: RunTerm;
  multiple: number | null;
  multipleTooltip: string | null;
  layerPremium: number | null;
  sharePremium: number | null;
  warningsTooltip: string | null;
  missingData: boolean;
  warningLevel: WarningLevel | undefined;
  overrideType: "multiple" | "premium" | null;
  /** Pre-override multiple (null for mixed/direct-premium peril types or rows without parts). */
  originalMultiple: number | null;
  /** Pre-override layer premium (null for rows without parts). */
  originalLayerPremium: number | null;
  /** Pre-override share premium (null for rows without parts). */
  originalSharePremium: number | null;
  /** True when an override update is pending and derived values are not yet recomputed by the backend. */
  stale: boolean;
  /**
   * Peril code that edits on this source-cell write to. Set when the cell
   * represents exactly one code (single-code peril-type rows, or peril-code
   * sub-rows). `null` when this source-cell has 0 codes (no data) or aggregates
   * multiple codes (multi-code peril-type rows) — in which case the cell is
   * read-only and edits must happen on the corresponding sub-row.
   */
  editablePerilCode: string | null;
  /**
   * Whether the underlying code accepts a `multiple` override (i.e. is AAL-
   * based, not direct-premium).
   */
  acceptsMultipleOverride: boolean;
};

type PremiumPerilRowBase = {
  layer: client.Layer;
  layerId: string;
  air: SourcePremiumData;
  rms: SourcePremiumData;
  spatialkat: SourcePremiumData;
  rms_22: SourcePremiumData;
  rms_22_air_50_50: SourcePremiumData;
  /** Premium data for the "Active Results" columns (the report's picks). */
  resolved: SourcePremiumData;
  /** Premium data for the "Luminor Blend View" columns (fixed sources). */
  luminorBlend: SourcePremiumData;
  target: number | null;
  isFirstInGroup: boolean;
};

type PerilTypePremiumPerilRow = PremiumPerilRowBase & {
  rowKind: "perilType";
  peril: DBPerilType;
  resolvedSource: PerilDataSource;
  partSources: DisplayPerilDataSource[];
  blendSource: PerilDataSource;
  blendPartSources: DisplayPerilDataSource[];
};
type PerilCodePremiumPerilRow = PremiumPerilRowBase & {
  rowKind: "perilCode";
  peril: DBPerilType;
  perilCode: string;
  resolvedSource: PerilDataSource;
  blendSource: PerilDataSource;
};
type CommissionAdjPerilRow = PremiumPerilRowBase & {
  rowKind: "commissionAdj";
  peril: "Commission Adj.";
  factor: number;
  commissionPercent: number | null;
};
type TotalPerilRow = PremiumPerilRowBase & {
  rowKind: "total";
  peril: "Total";
  // The modeled-loss columns are scoped to the modeled cat perils, so they use
  // these instead of the row's whole-table fields. `catResolved` drives the
  // AAL / Multiple / Loss-Ratio cells (and the comparison-column denominator);
  // `catLrAal` holds each source's cat AAL (the comparison-column numerators).
  catResolved: SourcePremiumData;
  catLuminorBlend: SourcePremiumData;
  catLrAal: Record<PerilDataSource, number | null>;
};
type PremiumPerilRow =
  | TotalPerilRow
  | CommissionAdjPerilRow
  | PerilCodePremiumPerilRow
  | PerilTypePremiumPerilRow;

const displayPerilDataSource = (
  perilCode: string,
  resolvedSource: PerilDataSource
): DisplayPerilDataSource =>
  perilCode === "AOP" ? "lum" : perilCode === "EBP" ? "eqbd" : resolvedSource;

/** The cross-source rollups a row shows side by side. */
type RollupView = "resolved" | "luminorBlend";

const displayPerilDataSource2 = (
  rowData: PerilTypePremiumPerilRow,
  view: RollupView
): DisplayPerilDataSource => {
  const partSources =
    view === "resolved" ? rowData.partSources : rowData.blendPartSources;
  return rowData.peril === "equipment_breakdown"
    ? "eqbd"
    : partSources.length > 1
      ? "mix"
      : partSources[0]!;
};

const formatPercent = (ratio: number): string => `${(ratio * 100).toFixed(1)}%`;

/** Peril names as this card shows them: SCS is abbreviated to save width. */
const layersPerilName = (peril: DBPerilType): string =>
  peril === "scs" ? "Wind / SCS" : readableNameFromPeril(peril);

/** A model's AAL usable for a loss-ratio comparison: null for a missing stub
 * (the model didn't run) so callers render a dash rather than a misleading
 * "0.0%". */
const usableAal = (data: SourcePremiumData): number | null =>
  data.missingData ? null : data.aal;

/** The rollup view the modeled-loss columns (AAL, Multiple, Loss Ratio) read.
 * For the Total row that's the cat-only rollup; every other row uses its own
 * view. The premium columns always use the full view. */
const lossView = (
  rowData: PremiumPerilRow,
  view: RollupView
): SourcePremiumData => {
  if (rowData.rowKind !== "total") return rowData[view];
  return view === "resolved" ? rowData.catResolved : rowData.catLuminorBlend;
};

const CAT_SCOPE_TOOLTIP =
  "Modeled cat perils only (Named Storm, Earthquake, Flood, Wildfire, " +
  "Wind/SCS) — excludes All Other Perils, Winter Storm, and equipment breakdown.";

/** Flag a Total cell whose value covers the modeled cat perils only (not the
 * whole-table premium), distinguishing it from the premium Total cells. */
const withCatMarker = (
  rowData: PremiumPerilRow,
  content: React.ReactNode
): React.ReactNode =>
  rowData.rowKind === "total" ? (
    <span className={styles.catMarked}>
      {content}
      <TooltipWrapper tooltipText={CAT_SCOPE_TOOLTIP}>
        <i className={`pi pi-cloud ${styles.catMarkerIcon}`} />
      </TooltipWrapper>
    </span>
  ) : (
    content
  );

// The per-model loss-ratio comparison columns, in display order — each shown as
// that source's AAL over the resolved layer premium (see modelLrBody).
const LR_MODEL_COLUMNS: { source: PerilDataSource; header: string }[] = [
  { source: "rms", header: "RMS 25" },
  { source: "rms_22", header: "RMS 22" },
  { source: "air", header: "AIR" },
  { source: "spatialkat", header: "KatRisk" },
];

function sortedPerilTypes(
  perilTypes: client.LayerPremiumBreakdown["peril_types"]
) {
  return Object.entries(perilTypes)
    .sort(([a], [b]) => {
      const ai = PERIL_TYPE_ORDER.indexOf(a as DBPerilType);
      const bi = PERIL_TYPE_ORDER.indexOf(b as DBPerilType);
      return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi);
    })
    .map(([, perilType]) => perilType);
}

const EMPTY_SOURCE_DATA: SourcePremiumData = {
  aal: null,
  annualAal: null,
  runTerm: null,
  multiple: null,
  multipleTooltip: null,
  layerPremium: null,
  sharePremium: null,
  warningsTooltip: null,
  missingData: false,
  warningLevel: undefined,
  overrideType: null,
  originalMultiple: null,
  originalLayerPremium: null,
  originalSharePremium: null,
  stale: false,
  editablePerilCode: null,
  acceptsMultipleOverride: false,
};

const EMPTY_TOTALS: client.LayerTotals = {
  layer_total: 0,
  share_total: 0,
  has_missing_data: false,
};

const norm = (v: number | null | undefined): number | undefined =>
  v != null && v !== 0 ? v : undefined;

/** Tooltip line for a missing part: the backend's specific reason when it
 * provides one, else the generic per-code line. */
const missingDataLine = (part: client.PerilCodePremium): string =>
  part.missing_reason ??
  `Missing data for ${perilCodeDisplayName(part.peril_code)}`;

/** Build SourcePremiumData for a single peril-code part. `runTerm` is the
 * enclosing peril type's, since the run identity is recorded per source. */
function buildCodeSourceData(
  part: client.PerilCodePremium,
  resolvedOv: client.PerilOverride | undefined,
  runTerm: RunTerm
): SourcePremiumData {
  const backendMultiple =
    part.override_type === "multiple" ? part.override_multiple : undefined;
  const backendPremium =
    part.override_type === "premium" ? part.override_premium : undefined;
  const stale =
    norm(resolvedOv?.multiple) !== norm(backendMultiple) ||
    norm(resolvedOv?.premium) !== norm(backendPremium);

  // Effective values from backend (post-override).
  let overrideType: "multiple" | "premium" | null = part.override_type ?? null;
  let effectiveMultiple: number | null;
  if (part.override_type === "multiple" && part.override_multiple != null) {
    effectiveMultiple = part.override_multiple;
  } else if (
    part.override_type === "premium" &&
    part.override_premium != null
  ) {
    // Direct-premium codes have no meaningful multiple even when overridden.
    effectiveMultiple =
      part.multiplier != null && part.aal > 0
        ? part.override_premium / part.aal
        : null;
  } else {
    effectiveMultiple = part.multiplier;
  }
  let effectiveLayerPremium: number = part.layer_effective;

  // Optimistic overlay while a mutation is in flight.
  if (stale) {
    if (norm(resolvedOv?.multiple) != null) {
      overrideType = "multiple";
      effectiveMultiple = resolvedOv!.multiple!;
    } else if (norm(resolvedOv?.premium) != null) {
      overrideType = "premium";
      effectiveLayerPremium = resolvedOv!.premium!;
    } else {
      overrideType = null;
      effectiveMultiple = part.multiplier;
    }
  }

  const warningLines = Object.entries(part.warnings).map(
    ([key, count]) =>
      `${WARNING_DISPLAY_NAMES[key] ?? key} (${count} location${count !== 1 ? "s" : ""})`
  );
  if (part.missing) {
    warningLines.push(missingDataLine(part));
  }

  return {
    aal: part.aal,
    annualAal: part.annual_aal,
    runTerm,
    multiple: effectiveMultiple,
    multipleTooltip: part.multiplier != null ? part.multiplier_reason : null,
    layerPremium: effectiveLayerPremium,
    sharePremium: part.share_effective,
    warningsTooltip: warningLines.length > 0 ? warningLines.join("\n") : null,
    missingData: part.missing,
    warningLevel: maxWarningLevel(Object.keys(part.warnings)),
    overrideType,
    originalMultiple: overrideType ? part.multiplier : null,
    originalLayerPremium: overrideType ? part.layer_premium : null,
    originalSharePremium: overrideType ? part.share_premium : null,
    stale,
    editablePerilCode: part.peril_code,
    acceptsMultipleOverride: part.multiplier !== null,
  };
}

/** Build SourcePremiumData for one view of a peril type — either a real
 * source or the resolved view. Single-code peril types proxy to the code;
 * multi-code peril types aggregate and are read-only at the type level. */
function buildPerilTypeSourceData(
  opt: client.PerilTypePremium,
  resolvedLayer: client.Layer
): SourcePremiumData {
  const parts = opt.parts;

  if (parts.length === 1) {
    const part = parts[0]!;
    const resolvedOv = resolvedLayer.peril_overrides?.[part.peril_code];
    return buildCodeSourceData(part, resolvedOv, opt.run_term);
  }

  const missingDataLines: string[] = [];
  const warningLines: string[] = [];
  const multipleTooltipLines: string[] = [];
  const allWarningKeys: string[] = [];
  let anyStale = false;

  for (const part of parts) {
    const ov = resolvedLayer.peril_overrides?.[part.peril_code];
    const backendMul =
      part.override_type === "multiple" ? part.override_multiple : undefined;
    const backendPrem =
      part.override_type === "premium" ? part.override_premium : undefined;
    if (
      norm(ov?.multiple) !== norm(backendMul) ||
      norm(ov?.premium) !== norm(backendPrem)
    ) {
      anyStale = true;
    }
    if (part.missing) {
      missingDataLines.push(missingDataLine(part));
    }
    for (const [warnKey, count] of Object.entries(part.warnings)) {
      const label = WARNING_DISPLAY_NAMES[warnKey] ?? warnKey;
      warningLines.push(
        `${label} (${count} location${count !== 1 ? "s" : ""})`
      );
      allWarningKeys.push(warnKey);
    }
    if (part.multiplier != null) {
      multipleTooltipLines.push(
        `${perilCodeDisplayName(part.peril_code)}: ${part.multiplier.toFixed(1)}x (${part.multiplier_reason})`
      );
    }
  }

  const allWarnings = [...warningLines, ...missingDataLines];
  const warningsTooltip =
    allWarnings.length > 0 ? allWarnings.join("\n") : null;
  const multipleTooltip =
    multipleTooltipLines.length === 0 ? null : multipleTooltipLines.join("\n");

  return {
    aal: opt.total_aal ?? 0,
    annualAal: opt.total_annual_aal,
    runTerm: opt.run_term,
    multiple: opt.multiple_effective,
    multipleTooltip,
    layerPremium: opt.layer_effective,
    sharePremium: opt.share_effective,
    warningsTooltip,
    missingData: missingDataLines.length > 0,
    warningLevel: maxWarningLevel(allWarningKeys),
    overrideType: null,
    originalMultiple: null,
    originalLayerPremium: null,
    originalSharePremium: null,
    stale: anyStale,
    editablePerilCode: null,
    acceptsMultipleOverride: false,
  };
}

export const LayersCard: React.FC<LayersCardProps> = ({
  specialtyPropertyInfo,
  title,
}) => {
  const isLightMode = useContext(ThemeContext).theme === "light";
  const sortedLayers = sortedLayersFromReport(specialtyPropertyInfo);
  const updateReport = useUpdateReport(specialtyPropertyInfo.report_id);

  // ── Portfolio Marginal Impact (per-layer dialog) ──
  // Eng-only while the metric is validated. Fetched by the dialog on open
  // (no page-load prefetch — most visits never open it, per PR review) at
  // the layer's own participation.
  const localUser = useLocalUser();
  const pmiEnabled = useFeatureEnabled(
    "portfolio-marginal-impact",
    undefined,
    localUser
  );
  const [pmiLayerId, setPmiLayerId] = useState<string | null>(null);

  const [sourcesExpanded, setSourcesExpanded] = useState(false);
  const [bulkOverrideLayerId, setBulkOverrideLayerId] = useState<string | null>(
    null
  );
  const [gateExposureLayerId, setGateExposureLayerId] = useState<string | null>(
    null
  );
  const [auditLayerId, setAuditLayerId] = useState<string | null>(null);

  // Layer dialog state — shared between add and edit modes
  const [showLayerDialog, setShowLayerDialog] = useState(false);
  const [editingLayerId, setEditingLayerId] = useState<string | null>(null);
  const [newStartingPoint, setNewStartingPoint] = useState<number | null>(null);
  const [newRange, setNewRange] = useState<number | null>(null);
  const [newParticipation, setNewParticipation] = useState<number | null>(null);
  const [newTarget, setNewTarget] = useState<number | null>(null);
  const [newCompetitorPricing, setNewCompetitorPricing] = useState<
    client.CompetitorPricing[]
  >([]);
  const [newIncludedPerils, setNewIncludedPerils] = useState<
    client.PolicyInfo["included_perils"]
  >([]);
  const [newFormSelection, setNewFormSelection] = useState<NonNullable<
    client.PolicyInfo["form_selection"]
  > | null>(null);
  const [newCommission, setNewCommission] = useState<number | null>(null);

  const isEditing = editingLayerId !== null;

  // Layer terms default to the layer's override, else the report's policy info.
  const reportPolicyInfo = specialtyPropertyInfo.report_json.policy_info;
  const reportPerils = reportPolicyInfo?.included_perils ?? [];
  const reportForm = reportPolicyInfo?.form_selection ?? null;
  const reportCommission =
    reportPolicyInfo?.broker_commission_percentage ?? null;

  const perilsEqual = (
    a: client.PolicyInfo["included_perils"],
    b: client.PolicyInfo["included_perils"]
  ) => a.length === b.length && [...a].sort().join() === [...b].sort().join();

  // How many of a layer's policy-term overrides actually deviate from the
  // report default — the count shown as "N override(s)" on the layer.
  const layerOverrideCount = (layer: client.Layer): number => {
    let count = 0;
    if (
      layer.included_perils_override != null &&
      !perilsEqual(layer.included_perils_override, reportPerils)
    ) {
      count++;
    }
    if (
      layer.form_selection_override != null &&
      layer.form_selection_override !== reportForm
    ) {
      count++;
    }
    if (
      layer.broker_commission_percentage_override != null &&
      layer.broker_commission_percentage_override !== reportCommission
    ) {
      count++;
    }
    return count;
  };

  // Which terms in the open modal differ from the report default — drives the
  // "Overridden" markers and revert buttons on the Policy Terms tab.
  const perilsOverridden = !perilsEqual(newIncludedPerils, reportPerils);
  const formOverridden = newFormSelection !== reportForm;
  const commissionOverridden = newCommission !== reportCommission;

  const resetLayerDialog = () => {
    setShowLayerDialog(false);
    setEditingLayerId(null);
    setNewStartingPoint(null);
    setNewRange(null);
    setNewParticipation(null);
    setNewTarget(null);
    setNewCompetitorPricing([]);
    setNewIncludedPerils([]);
    setNewFormSelection(null);
    setNewCommission(null);
  };

  const openAddDialog = () => {
    setEditingLayerId(null);
    setNewStartingPoint(null);
    setNewRange(null);
    setNewParticipation(null);
    setNewTarget(null);
    setNewCompetitorPricing([]);
    setNewIncludedPerils(reportPolicyInfo?.included_perils ?? []);
    setNewFormSelection(reportPolicyInfo?.form_selection ?? null);
    setNewCommission(reportPolicyInfo?.broker_commission_percentage ?? null);
    setShowLayerDialog(true);
  };

  const openEditDialog = (layer: client.Layer) => {
    setEditingLayerId(layer.layer_id ?? null);
    setNewStartingPoint(layer.starting_point);
    setNewRange(layer.range);
    setNewParticipation(layer.participation ?? null);
    setNewTarget(layer.target);
    setNewCompetitorPricing(
      (layer.competitor_pricing ?? []).map((c) => ({ ...c }))
    );
    setNewIncludedPerils(
      layer.included_perils_override ?? reportPolicyInfo?.included_perils ?? []
    );
    setNewFormSelection(
      layer.form_selection_override ?? reportPolicyInfo?.form_selection ?? null
    );
    setNewCommission(
      layer.broker_commission_percentage_override ??
        reportPolicyInfo?.broker_commission_percentage ??
        null
    );
    setShowLayerDialog(true);
  };

  // Inline editing of a layer's target premium or participation (click the
  // value in the meta row). Participation is edited as a percent of the
  // layer's range because that is how the meta row displays it. Enter saves;
  // the resulting unmount can still fire the input's blur, so the flag keeps
  // that second call from saving again.
  const [inlineEdit, setInlineEdit] = useState<{
    layerId: string;
    field: "target" | "participation";
    value: number | null;
  } | null>(null);
  const inlineEditClosed = useRef(false);

  const openInlineEdit = (
    layer: client.Layer,
    field: "target" | "participation"
  ) => {
    if (layer.layer_id == null) return;
    inlineEditClosed.current = false;
    setInlineEdit({
      layerId: layer.layer_id,
      field,
      value: field === "target" ? layer.target : participationPercent(layer),
    });
  };

  const closeInlineEdit = () => {
    inlineEditClosed.current = true;
    setInlineEdit(null);
  };

  const saveInlineEdit = async (layer: client.Layer) => {
    if (inlineEditClosed.current || inlineEdit == null) return;
    const { field, value } = inlineEdit;
    closeInlineEdit();
    const newLayers = cloneDeep(specialtyPropertyInfo.report_json.layer_info);
    const existing = newLayers.find((L) => L.layer_id === layer.layer_id);
    if (!existing) return;
    if (field === "target") {
      if (value === layer.target) return;
      existing.target = value;
    } else {
      if (value === participationPercent(layer)) return;
      // Blank means "same as limit", matching the layer dialog. Pricing
      // treats a zero participation as full participation, so store null
      // rather than a 0 that would display "0%" but price at 100%.
      const participation =
        value == null ? null : Math.round((value / 100) * layer.range);
      existing.participation =
        participation != null && participation > 0 ? participation : null;
    }
    await updateReport.mutateAsync({ layerInfo: newLayers });
  };

  const competitorPricingValid = newCompetitorPricing.every(
    (c) => c.carrier_name.trim().length > 0
  );

  const handleSaveLayer = async () => {
    if (newStartingPoint == null || newRange == null || newRange <= 0) return;
    if (!competitorPricingValid) return;
    const cleanedCompetitorPricing = newCompetitorPricing.map((c) => ({
      carrier_name: c.carrier_name.trim(),
      price: c.price ?? null,
      notes: c.notes?.trim() ? c.notes.trim() : null,
    }));
    const newLayers = cloneDeep(specialtyPropertyInfo.report_json.layer_info);

    if (isEditing) {
      const existing = newLayers.find((L) => L.layer_id === editingLayerId);
      if (!existing) return;
      existing.participation = newParticipation;
      existing.target = newTarget;
      existing.competitor_pricing = cleanedCompetitorPricing;
      // Store overrides only where they deviate from the report; matching the
      // report default clears the override so the layer inherits it live.
      existing.included_perils_override = perilsOverridden
        ? newIncludedPerils
        : null;
      existing.form_selection_override = formOverridden
        ? newFormSelection
        : null;
      existing.broker_commission_percentage_override = commissionOverridden
        ? newCommission
        : null;
    } else {
      newLayers.push({
        layer_id: uuid(),
        starting_point: newStartingPoint,
        range: newRange,
        target: newTarget,
        rated: null,
        participation: newParticipation,
        layer_source: "manual",
        is_enabled: true,
        peril_overrides: null,
        competitor_pricing: cleanedCompetitorPricing,
        included_perils_override: perilsOverridden ? newIncludedPerils : null,
        form_selection_override: formOverridden ? newFormSelection : null,
        broker_commission_percentage_override: commissionOverridden
          ? newCommission
          : null,
      });
    }

    await updateReport.mutateAsync({ layerInfo: newLayers });
    resetLayerDialog();
  };

  // Live preview of the layer description as user fills out the form
  const layerPreview = useMemo(() => {
    if (newRange == null || newRange <= 0) return null;
    return readableLimitOfLiabilityFromLayer({
      starting_point: newStartingPoint ?? 0,
      range: newRange,
      target: null,
      rated: null,
      participation: newParticipation,
    });
  }, [newStartingPoint, newRange, newParticipation]);

  // Per vendor, the vendor data its live run modeled with and which it could
  // switch to. The dialog switches; the Enrichment column shows what is live.
  const { data: vendorEnrichment } = useCatVendorEnrichment(
    specialtyPropertyInfo.report_id
  );
  const liveSourceByVendor = useMemo(
    () => liveSourcesByVendor(vendorEnrichment),
    [vendorEnrichment]
  );
  const [showVendorDataDialog, setShowVendorDataDialog] = useState(false);

  // Fetch calculated premiums from backend
  const {
    data: premiumData,
    isLoading: firstPremiumLoading,
    isFetching: premiumLoading,
    isError: premiumError,
  } = useCalculatedPremium(specialtyPropertyInfo.report_id, true);

  // The peril sources currently in effect — drives the integration-status
  // banner.
  const selectedPerilSources = useSelectedPerilSources(specialtyPropertyInfo);

  // Build a lookup from backend premium data: layer_id -> LayerPremiumBreakdown
  const premiumByLayer = useMemo(() => {
    if (!premiumData?.layers?.length)
      return new Map<string, client.LayerPremiumBreakdown>();
    return new Map<string, client.LayerPremiumBreakdown>(
      premiumData.layers.map((l) => [l.layer_id, l])
    );
  }, [premiumData]);

  // Premium table state — keyed by layer_id
  const layerOptions = sortedLayers.map((l) => ({
    label: `${readableLayer(l)} (${l.layer_source})`,
    value: l.layer_id!,
  }));
  const enabledLayers = specialtyPropertyInfo.report_json.layer_info
    .filter((L) => L.is_enabled)
    .map((L) => L.layer_id);

  // Resolve layer info, preferring optimistic pending mutation data
  const resolvedLayerInfo = useMemo(() => {
    const layers = specialtyPropertyInfo.report_json.layer_info;
    if (!updateReport.isPending || !updateReport.variables?.layerInfo)
      return layers;
    return updateReport.variables.layerInfo;
  }, [
    specialtyPropertyInfo.report_json.layer_info,
    updateReport.isPending,
    updateReport.variables,
  ]);

  const handlePerilOverrideChange = async (
    layerId: string,
    perilCode: string,
    override: client.PerilOverride | undefined
  ) => {
    const layer = specialtyPropertyInfo.report_json.layer_info.find(
      (L) => L.layer_id === layerId
    );
    if (!layer) return;

    const existing = layer.peril_overrides?.[perilCode];
    if (override === undefined && existing === undefined) return;
    if (
      override !== undefined &&
      existing !== undefined &&
      existing.multiple === override.multiple &&
      existing.premium === override.premium
    )
      return;

    const newLayers = cloneDeep(specialtyPropertyInfo.report_json.layer_info);
    const newLayer = newLayers.find((L) => L.layer_id === layerId)!;

    newLayer.peril_overrides = newLayer.peril_overrides ?? {};

    if (override !== undefined) {
      newLayer.peril_overrides[perilCode] = override;
    } else {
      delete newLayer.peril_overrides[perilCode];
    }

    await updateReport.mutateAsync({ layerInfo: newLayers });
  };

  /**
   * Write a `premium` override per peril code for the chosen layer in a
   * single mutation. The codes come straight from the dialog, which walked the
   * resolved view's parts to build its sliders.
   */
  const handleBulkPremiumApply = async (
    layerId: string,
    targets: Array<{ perilCode: string; premium: number }>
  ) => {
    const newLayers = cloneDeep(specialtyPropertyInfo.report_json.layer_info);
    const newLayer = newLayers.find((L) => L.layer_id === layerId);
    if (!newLayer) return;

    newLayer.peril_overrides = newLayer.peril_overrides ?? {};
    for (const { perilCode, premium } of targets) {
      newLayer.peril_overrides[perilCode] = { premium };
    }

    await updateReport.mutateAsync({ layerInfo: newLayers });
  };

  const premiumRows = useMemo((): PremiumPerilRow[] => {
    const rows: PremiumPerilRow[] = [];

    for (const layer of sortedLayers) {
      const layerId = layer.layer_id!;
      if (!enabledLayers.includes(layerId)) continue;
      const layerPremium = premiumByLayer.get(layerId);
      const resolvedLayer = resolvedLayerInfo.find(
        (L) => L.layer_id === layerId
      );
      if (!resolvedLayer) continue;

      let isFirst = true;
      if (!layerPremium) continue;
      for (const perilTypeBreakdown of sortedPerilTypes(
        layerPremium.peril_types
      )) {
        const resolvedSource = perilTypeBreakdown.resolved.source;
        const blendSource = perilTypeBreakdown.luminor_blend.source;
        const perilType = perilTypeBreakdown.resolved.peril_type;

        const premiumsBySource: {
          [key: string]: SourcePremiumData | undefined;
        } = {};
        for (const [source, perilTypeOption] of Object.entries(
          perilTypeBreakdown.by_source
        )) {
          premiumsBySource[source] = buildPerilTypeSourceData(
            perilTypeOption,
            resolvedLayer
          );
        }
        // The resolved view's parts mirror the picked source's data but with
        // LR-distributed shares, so the per-peril rows in the resolved column
        // reconcile to share_total exactly.
        const resolved = buildPerilTypeSourceData(
          perilTypeBreakdown.resolved,
          resolvedLayer
        );
        const luminorBlend = buildPerilTypeSourceData(
          perilTypeBreakdown.luminor_blend,
          resolvedLayer
        );

        const perilSourceData = {
          air: premiumsBySource["air"] ?? EMPTY_SOURCE_DATA,
          rms: premiumsBySource["rms"] ?? EMPTY_SOURCE_DATA,
          spatialkat: premiumsBySource["spatialkat"] ?? EMPTY_SOURCE_DATA,
          rms_22: premiumsBySource["rms_22"] ?? EMPTY_SOURCE_DATA,
          rms_22_air_50_50:
            premiumsBySource["rms_22_air_50_50"] ?? EMPTY_SOURCE_DATA,
        };
        const parts = perilTypeBreakdown.resolved.parts;

        rows.push({
          layer: layer,
          layerId: layerId,
          peril: perilType,
          rowKind: "perilType",
          ...perilSourceData,
          resolvedSource,
          resolved,
          blendSource,
          luminorBlend,
          target: null,
          isFirstInGroup: isFirst,
          partSources: parts.map((part) =>
            displayPerilDataSource(part.peril_code, resolvedSource)
          ),
          blendPartSources: perilTypeBreakdown.luminor_blend.parts.map((part) =>
            displayPerilDataSource(part.peril_code, blendSource)
          ),
        });
        isFirst = false;

        // Emit peril-code sub-rows (one per peril code in this peril type).
        // Build the code list in a fixed, source-independent order: walk every
        // source's parts and append codes in first-seen order.
        const allCodes: string[] = [];
        const seenCodes = new Set<string>();
        const allOpts = [
          ...Object.values(perilTypeBreakdown.by_source),
          perilTypeBreakdown.resolved,
          perilTypeBreakdown.luminor_blend,
        ];
        for (const opt of allOpts) {
          for (const part of opt.parts) {
            if (!seenCodes.has(part.peril_code)) {
              seenCodes.add(part.peril_code);
              allCodes.push(part.peril_code);
            }
          }
        }
        // Only show sub-rows when there are multiple codes — a single code
        // would just duplicate the peril-type row above.
        if (allCodes.length > 1) {
          for (const code of allCodes) {
            const codeBySource: {
              [key: string]: SourcePremiumData | undefined;
            } = {};
            for (const [source, opt] of Object.entries(
              perilTypeBreakdown.by_source
            )) {
              const part = opt.parts.find((p) => p.peril_code === code);
              if (!part) continue;
              const ov = resolvedLayer.peril_overrides?.[code];
              codeBySource[source] = buildCodeSourceData(
                part,
                ov,
                opt.run_term
              );
            }
            const rollupCodeData = (
              view: client.PerilTypePremium
            ): SourcePremiumData => {
              const part = view.parts.find((p) => p.peril_code === code);
              return part
                ? buildCodeSourceData(
                    part,
                    resolvedLayer.peril_overrides?.[code],
                    view.run_term
                  )
                : EMPTY_SOURCE_DATA;
            };
            const codeSourceData = {
              air: codeBySource["air"] ?? EMPTY_SOURCE_DATA,
              rms: codeBySource["rms"] ?? EMPTY_SOURCE_DATA,
              spatialkat: codeBySource["spatialkat"] ?? EMPTY_SOURCE_DATA,
              rms_22: codeBySource["rms_22"] ?? EMPTY_SOURCE_DATA,
              rms_22_air_50_50:
                codeBySource["rms_22_air_50_50"] ?? EMPTY_SOURCE_DATA,
            };
            rows.push({
              layer: layer,
              layerId: layerId,
              peril: perilType,
              rowKind: "perilCode",
              perilCode: code,
              ...codeSourceData,
              resolvedSource,
              resolved: rollupCodeData(perilTypeBreakdown.resolved),
              blendSource,
              luminorBlend: rollupCodeData(perilTypeBreakdown.luminor_blend),
              target: null,
              isFirstInGroup: false,
            });
          }
        }
      }

      // Commission adjustment row — only shown when the factor is non-trivial.
      // The per-peril rows above are quoted at the default 17.5% commission,
      // while the Total row's premium comes from the backend's commission-
      // adjusted totals. This row makes the step between them visible: AAL stays
      // untouched while a multiplier scales the gross premium.
      const commissionFactor = layerPremium.commission_adjustment_factor;
      const commissionPercent =
        layerPremium.broker_commission_percentage ?? null;
      if (commissionFactor != null && commissionFactor !== 1.0) {
        rows.push({
          layer: layer,
          layerId: layerId,
          peril: "Commission Adj.",
          rowKind: "commissionAdj",
          factor: commissionFactor,
          commissionPercent,
          air: EMPTY_SOURCE_DATA,
          rms: EMPTY_SOURCE_DATA,
          spatialkat: EMPTY_SOURCE_DATA,
          rms_22: EMPTY_SOURCE_DATA,
          rms_22_air_50_50: EMPTY_SOURCE_DATA,
          resolved: EMPTY_SOURCE_DATA,
          luminorBlend: EMPTY_SOURCE_DATA,
          target: null,
          isFirstInGroup: false,
        });
      }

      // Total row. The premium columns represent the table contents — every
      // peril-type row in the layer. The modeled-loss columns (AAL, Multiple,
      // Loss Ratio, and the comparison columns) instead cover just the modeled
      // cat perils; see `catResolved` / `catLrAal` below.
      const layerRows = rows.filter(
        (r) => r.layerId === layerId && r.rowKind === "perilType"
      );
      // Null when a source modeled none of the given rows, so it dashes rather
      // than showing a misleading 0%.
      const buildTotalSourceData = (
        forRows: PremiumPerilRow[],
        get: (r: PremiumPerilRow) => SourcePremiumData,
        total: client.LayerTotals
      ): SourcePremiumData => {
        // Frontend calculations are usually wrong for this code, but total AAL
        // is not computed by the backend, so we have to do it here
        const aal = sumModeledAal(forRows.map((r) => usableAal(get(r))));
        const multiple = aalMultiple(total.layer_total, aal);
        // The annual total only means something when every modeled row has one;
        // a partial sum would understate it against the full TAL.
        const modeled = forRows.filter((r) => usableAal(get(r)) !== null);
        const annualAal =
          modeled.length > 0 && modeled.every((r) => get(r).annualAal !== null)
            ? modeled.reduce((s, r) => s + (get(r).annualAal ?? 0), 0)
            : null;
        return {
          aal,
          annualAal,
          runTerm: null,
          multiple,
          multipleTooltip: null,
          layerPremium: total.layer_total,
          sharePremium: total.share_total,
          missingData: aal === null,
          warningLevel: undefined,
          warningsTooltip: null,
          overrideType: null,
          originalMultiple: null,
          originalLayerPremium: null,
          originalSharePremium: null,
          stale: forRows.some((r) => get(r).stale),
          editablePerilCode: null,
          acceptsMultipleOverride: false,
        };
      };

      const totalSourceData = {
        air: buildTotalSourceData(
          layerRows,
          (r) => r.air,
          layerPremium.totals["air"] ?? EMPTY_TOTALS
        ),
        rms: buildTotalSourceData(
          layerRows,
          (r) => r.rms,
          layerPremium.totals["rms"] ?? EMPTY_TOTALS
        ),
        spatialkat: buildTotalSourceData(
          layerRows,
          (r) => r.spatialkat,
          layerPremium.totals["spatialkat"] ?? EMPTY_TOTALS
        ),
        rms_22: buildTotalSourceData(
          layerRows,
          (r) => r.rms_22,
          layerPremium.totals["rms_22"] ?? EMPTY_TOTALS
        ),
        rms_22_air_50_50: buildTotalSourceData(
          layerRows,
          (r) => r.rms_22_air_50_50,
          layerPremium.totals["rms_22_air_50_50"] ?? EMPTY_TOTALS
        ),
      };

      // The modeled-loss columns are scoped to the modeled cat perils (see
      // isModeledCatPeril in util.ts): the AAL/Multiple/Loss-Ratio cells use
      // `catResolved`, and each comparison column uses its source's `catLrAal`.
      const catRows = layerRows.filter(
        (r) => r.rowKind === "perilType" && isModeledCatPeril(r.peril)
      );
      // A model's cat total is shown only when it modeled every cat peril.
      // Otherwise it would compare a partial book against the full cat premium —
      // e.g. KatRisk only models flood — so we hide it (null → dash).
      const catTotalIfComplete = (
        get: (r: PremiumPerilRow) => number | null
      ): number | null => {
        if (!catRows.length) return null;
        let total = 0;
        for (const r of catRows) {
          const v = get(r);
          if (v === null) return null;
          total += v;
        }
        return total;
      };
      const catLrAal: Record<PerilDataSource, number | null> = {
        air: catTotalIfComplete((r) => usableAal(r.air)),
        rms: catTotalIfComplete((r) => usableAal(r.rms)),
        spatialkat: catTotalIfComplete((r) => usableAal(r.spatialkat)),
        rms_22: catTotalIfComplete((r) => usableAal(r.rms_22)),
        rms_22_air_50_50: catTotalIfComplete((r) =>
          usableAal(r.rms_22_air_50_50)
        ),
      };
      // The cat rollup has no backend total. Its Multiple and Loss Ratio are
      // measured against the technical (default-commission) premium to match
      // the per-peril rows, so this is the one place a frontend calculation is
      // correct.  Whole-layer views must instead use the backend's
      // commission-adjusted total. The same rollup feeds the referral tab's
      // pricing line, so the two surfaces agree by construction.
      const catRollup = (view: RollupView): SourcePremiumData => {
        const cat = aalRollup(
          catRows.map((r) => ({
            aal: usableAal(r[view]),
            premium: r[view].layerPremium ?? 0,
          }))
        );
        const catBaseTotal: client.LayerTotals = {
          layer_total: cat.premium,
          share_total: catRows.reduce(
            (s, r) =>
              usableAal(r[view]) !== null ? s + (r[view].sharePremium ?? 0) : s,
            0
          ),
          has_missing_data: false,
        };
        return buildTotalSourceData(catRows, (r) => r[view], catBaseTotal);
      };

      rows.push({
        layer: layer,
        layerId: layerId,
        peril: "Total",
        rowKind: "total",
        ...totalSourceData,
        resolved: buildTotalSourceData(
          layerRows,
          (r) => r.resolved,
          layerPremium.totals["resolved"] ?? EMPTY_TOTALS
        ),
        luminorBlend: buildTotalSourceData(
          layerRows,
          (r) => r.luminorBlend,
          layerPremium.totals["luminor_blend"] ?? EMPTY_TOTALS
        ),
        catLrAal,
        catResolved: catRollup("resolved"),
        catLuminorBlend: catRollup("luminorBlend"),
        target: layer.target,
        isFirstInGroup: false,
      });
    }
    return rows;
  }, [sortedLayers, enabledLayers, premiumByLayer, resolvedLayerInfo]);

  // Split the modeled loss into TAL and AAL only once every modeled cat peril
  // is priced on the policy term; see showTermColumns.
  const showTal = useMemo(
    () =>
      showTermColumns(
        premiumRows
          .filter(
            (r): r is PerilTypePremiumPerilRow =>
              r.rowKind === "perilType" && isModeledCatPeril(r.peril)
          )
          .map((r) => ({
            runTerm: r.resolved.runTerm,
            missingData: r.resolved.missingData,
          }))
      ),
    [premiumRows]
  );
  const lossHeader = showTal ? "TAL" : "AAL";
  const lossHeaderTooltip = showTal
    ? "Term Average Loss: the modeled loss over the policy period. Premium, Multiple and LR are priced on it."
    : undefined;
  const annualHeaderTooltip =
    "Annual Average Loss: the same exposure modeled over a 12-month term from the effective date.";

  const tiv = specialtyPropertyInfo.precomputed_metadata?.total_tiv;

  const rateBody = (layerPremium: number) => {
    if (!tiv) return "";
    const rate = (layerPremium / tiv) * 100;
    return (
      <span style={{ fontFamily: "monospace" }}>{formatTwoDecimals(rate)}</span>
    );
  };

  /** Column groups shown in the data table — one per modeling source. */
  const sources = [
    ["air", "AIR"],
    ["rms", "RMS"],
    ["spatialkat", "Kat Risk"],
  ] as const;

  const overrideBg = isLightMode
    ? HIGHLIGHT_USER_OVERRIDE_LIGHT_MODE
    : HIGHLIGHT_USER_OVERRIDE_DARK_MODE;

  /** Return the background color for warning/missing-data highlights. */
  const warningBg = (data: SourcePremiumData): string | undefined => {
    if (data.warningLevel === "critical")
      return isLightMode
        ? HIGHLIGHT_ERROR_LIGHT_MODE
        : HIGHLIGHT_ERROR_DARK_MODE;
    if (data.warningLevel === "info" || data.missingData)
      return isLightMode
        ? HIGHLIGHT_WARNING_LIGHT_MODE
        : HIGHLIGHT_WARNING_DARK_MODE;
    return undefined;
  };

  /** Whether a column source should be visually muted for the given row. */
  const isMuted = (
    columnSource: PerilDataSource,
    rowData: PremiumPerilRow
  ): boolean => {
    // For total / commission-adjustment rows (no resolvedSource), show all unmuted.
    if (rowData.rowKind === "total" || rowData.rowKind === "commissionAdj") {
      return false;
    }
    return columnSource !== rowData.resolvedSource;
  };

  // ── Shared column body renderers ──
  // These are used by the rollup columns and the provider detail columns
  // to avoid duplicating ~200 lines of render logic.

  const sourceBody = (rowData: PremiumPerilRow, view: RollupView) => {
    if (rowData.rowKind === "total") return "";
    if (rowData.rowKind === "commissionAdj") return "";
    const source =
      view === "resolved" ? rowData.resolvedSource : rowData.blendSource;
    if (!source) return "";

    const display =
      rowData.rowKind === "perilCode"
        ? displayPerilDataSource(rowData.perilCode, source)
        : displayPerilDataSource2(rowData, view);

    // Label RMS-sourced rows with the explicit version ("RMS 22" / "RMS 25"),
    // falling back to plain "RMS" when the run reports no version.
    const label =
      display === "rms"
        ? rmsSourceLabel(
            premiumByLayer.get(rowData.layerId)?.peril_types[rowData.peril]
              ?.by_source?.["rms"]?.model_profile
          )
        : undefined;

    return <SourceBadge source={display} label={label} />;
  };

  const aalBody = (data: SourcePremiumData) => {
    const bg = data.multiple !== null ? warningBg(data) : undefined;
    return (
      <span
        className={bg ? styles.highlightPill : undefined}
        style={{ fontFamily: "monospace", backgroundColor: bg }}
      >
        {data.multiple === null
          ? ""
          : data.aal !== null
            ? formatExactMoneyDollars(data.aal, {
                withRounding: true,
                withDollarSign: true,
              })
            : "-"}
      </span>
    );
  };

  /** The annual loss beside a policy-term loss. Blank on rows without a
   * modeled AAL (like `aalBody`); a dash when the annual companion has not
   * produced one. */
  const annualAalBody = (data: SourcePremiumData) => {
    if (data.multiple === null) return "";
    return (
      <span style={{ fontFamily: "monospace" }}>
        {data.annualAal !== null
          ? formatExactMoneyDollars(data.annualAal, {
              withRounding: true,
              withDollarSign: true,
            })
          : "-"}
      </span>
    );
  };

  /** Loss Ratio cell (AAL ÷ Layer Prem). Blank for rows without a modeled AAL
   * (AOP, the "All Other Perils" aggregate, commission adjustment). */
  const lossRatioBody = (rowData: PremiumPerilRow, view: RollupView) => {
    if (rowData.rowKind === "commissionAdj") return "";
    const data = lossView(rowData, view);
    if (data.stale) return "-";
    if (
      data.multiple === null ||
      data.aal === null ||
      data.layerPremium === null ||
      data.layerPremium === 0
    ) {
      return "";
    }
    return (
      <span style={{ fontFamily: "monospace" }}>
        {formatPercent(data.aal / data.layerPremium)}
      </span>
    );
  };

  /** Loss-ratio cell for a model column: `numerator AAL ÷ resolved Layer
   * Premium` (every column divides by the same `resolved`, so they compare
   * directly). Blank on commission / peril-code sub-rows and on rows with no
   * clean modeled premium; a dash when this model has no AAL for the peril. */
  const modelLrBody = (
    numerator: number | null,
    rowData: PremiumPerilRow,
    resolved: SourcePremiumData
  ) => {
    if (rowData.rowKind === "commissionAdj" || rowData.rowKind === "perilCode")
      return "";
    if (resolved.stale) return "-";
    // Rows without a clean modeled premium (AOP / mixed) have no loss ratio at
    // all — leave blank, matching the resolved Loss Ratio column.
    if (
      resolved.multiple === null ||
      resolved.layerPremium === null ||
      resolved.layerPremium === 0
    ) {
      return "";
    }
    // No modeled AAL for this peril → dash; a modeled (even zero) AAL shows.
    if (numerator === null) return <span className={styles.lrEmpty}>–</span>;
    return (
      <span style={{ fontFamily: "monospace" }}>
        {formatPercent(numerator / resolved.layerPremium)}
      </span>
    );
  };

  /** `modelLrBody` for one source, reading the Total row's cat-only rollup. */
  const sourceLrBody = (source: PerilDataSource, rowData: PremiumPerilRow) =>
    withCatMarker(
      rowData,
      rowData.rowKind === "total"
        ? modelLrBody(rowData.catLrAal[source], rowData, rowData.catResolved)
        : modelLrBody(usableAal(rowData[source]), rowData, rowData.resolved)
    );

  const multipleBody = (rowData: PremiumPerilRow, data: SourcePremiumData) => {
    const isOverridden = data.overrideType === "multiple";
    const isDerived = data.overrideType === "premium";
    if (data.stale && !isOverridden) return "-";
    const multipleText = data.multiple != null ? data.multiple.toFixed(2) : "-";
    const inner = (
      <span
        className={isOverridden ? styles.highlightPill : undefined}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          fontFamily: "monospace",
          fontStyle: isDerived ? "italic" : undefined,
          backgroundColor: isOverridden ? overrideBg : undefined,
        }}
      >
        {multipleText}
        {isOverridden && (
          <i
            className="pi pi-times"
            style={{ fontSize: "0.65rem", cursor: "pointer", opacity: 0.7 }}
            onClick={(e) => {
              e.stopPropagation();
              if (data.editablePerilCode) {
                handlePerilOverrideChange(
                  rowData.layerId,
                  data.editablePerilCode,
                  undefined
                );
              }
            }}
          />
        )}
      </span>
    );
    const tooltipText =
      isOverridden && data.originalMultiple != null
        ? `A user edited this value. The previous value was ${data.originalMultiple.toFixed(2)}`
        : isDerived && data.originalMultiple != null
          ? `A user override affected this value. The previous value was ${data.originalMultiple.toFixed(2)}`
          : data.multipleTooltip;
    if (!tooltipText) return inner;
    return (
      <TooltipWrapper
        tooltipText={tooltipText}
        tooltipProps={{ pt: { text: { style: { whiteSpace: "pre" } } } }}
      >
        {inner}
      </TooltipWrapper>
    );
  };

  const layerPremBody = (rowData: PremiumPerilRow, data: SourcePremiumData) => {
    const isOverridden = data.overrideType === "premium";
    const isDerived = data.overrideType === "multiple";
    if (data.stale && !isOverridden) return "-";
    const bg = isOverridden ? overrideBg : warningBg(data);
    const value = (
      <span
        className={bg ? styles.highlightPill : undefined}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          fontFamily: "monospace",
          fontStyle: isDerived ? "italic" : undefined,
          backgroundColor: bg,
        }}
      >
        {data.layerPremium !== null
          ? formatExactMoneyDollars(data.layerPremium, {
              withRounding: true,
              withDollarSign: true,
            })
          : "-"}
        {isOverridden && (
          <i
            className="pi pi-times"
            style={{ fontSize: "0.65rem", cursor: "pointer", opacity: 0.7 }}
            onClick={(e) => {
              e.stopPropagation();
              if (data.editablePerilCode) {
                handlePerilOverrideChange(
                  rowData.layerId,
                  data.editablePerilCode,
                  undefined
                );
              }
            }}
          />
        )}
      </span>
    );
    const wrappedValue =
      isOverridden && data.originalLayerPremium != null ? (
        <TooltipWrapper
          tooltipText={`A user edited this value. The previous value was ${formatExactMoneyDollars(
            data.originalLayerPremium,
            { withRounding: true, withDollarSign: true }
          )}`}
        >
          {value}
        </TooltipWrapper>
      ) : isDerived && data.originalLayerPremium != null ? (
        <TooltipWrapper
          tooltipText={`A user override affected this value. The previous value was ${formatExactMoneyDollars(
            data.originalLayerPremium,
            { withRounding: true, withDollarSign: true }
          )}`}
        >
          {value}
        </TooltipWrapper>
      ) : (
        value
      );
    if (!data.warningsTooltip) return wrappedValue;
    return (
      <span
        style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}
      >
        {wrappedValue}
        <TooltipWrapper tooltipText={data.warningsTooltip}>
          <i
            className="pi pi-question-circle"
            style={{
              fontSize: "0.75rem",
              color: "var(--text-color-secondary)",
              cursor: "help",
            }}
          />
        </TooltipWrapper>
      </span>
    );
  };

  const sharePremBody = (data: SourcePremiumData) => {
    if (data.stale) return "-";
    const isDerived = data.overrideType != null;
    const bg = warningBg(data);
    const inner = (
      <span
        className={bg ? styles.highlightPill : undefined}
        style={{
          fontFamily: "monospace",
          fontStyle: isDerived ? "italic" : undefined,
          backgroundColor: bg,
        }}
      >
        {data.sharePremium !== null
          ? formatExactMoneyDollars(data.sharePremium, {
              withRounding: true,
              withDollarSign: true,
            })
          : "-"}
      </span>
    );
    if (isDerived && data.originalSharePremium != null) {
      return (
        <TooltipWrapper
          tooltipText={`A user override affected this value. The previous value was ${formatExactMoneyDollars(
            data.originalSharePremium,
            { withRounding: true, withDollarSign: true }
          )}`}
        >
          {inner}
        </TooltipWrapper>
      );
    }
    return inner;
  };

  /** Whether the Multiple cell is editable for this source-cell. */
  const isMultipleEditable = (data: SourcePremiumData): boolean => {
    return data.editablePerilCode != null && data.acceptsMultipleOverride;
  };

  /** Whether the Layer Premium cell is editable for this source-cell. */
  const isLayerPremEditable = (data: SourcePremiumData): boolean => {
    return data.editablePerilCode != null;
  };

  /** Run `apply`, unless the resulting AAL multiple would fall below the floor —
   * in which case confirm first. A null multiple (no meaningful multiple, or
   * clearing an override) skips the check. */
  const applyWithMultipleFloorCheck = (
    resultingMultiple: number | null,
    apply: () => void
  ) => {
    if (resultingMultiple != null && resultingMultiple < MIN_MULTIPLE) {
      confirmDialog({
        message: `This would put the AAL multiple below the ${MIN_MULTIPLE} floor. Are you sure you want to continue?`,
        header: "Below multiple floor",
        acceptLabel: "Continue",
        rejectLabel: "Cancel",
        defaultFocus: "reject",
        accept: apply,
      });
    } else {
      apply();
    }
  };

  /** Shared edit handler for the Multiple column. */
  const multipleEditComplete = (
    options: { rowData: PremiumPerilRow; newValue: unknown },
    getData: (r: PremiumPerilRow) => SourcePremiumData
  ) => {
    const rowData = options.rowData;
    const data = getData(rowData);
    if (!isMultipleEditable(data)) return;
    const newValue = options.newValue as number | null | undefined;
    if (newValue === undefined) return;
    const editorInit = data.multiple;
    if (
      newValue == editorInit ||
      (newValue != null &&
        editorInit != null &&
        Math.abs(newValue - editorInit) < 0.001)
    )
      return;
    applyWithMultipleFloorCheck(newValue ?? null, () =>
      handlePerilOverrideChange(
        rowData.layerId,
        data.editablePerilCode!,
        newValue != null ? { multiple: newValue } : undefined
      )
    );
  };

  /** Shared edit handler for the Layer Premium column. */
  const layerPremEditComplete = (
    options: { rowData: PremiumPerilRow; newValue: unknown },
    getData: (r: PremiumPerilRow) => SourcePremiumData
  ) => {
    const rowData = options.rowData;
    const data = getData(rowData);
    if (!isLayerPremEditable(data)) return;
    const newValue = options.newValue as number | null | undefined;
    if (newValue === undefined) return;
    if (newValue == data.layerPremium) return;
    // A premium override back-derives the multiple (premium / AAL); only check
    // the floor for perils with a real AAL-based multiple.
    const derivedMultiple =
      newValue != null &&
      data.acceptsMultipleOverride &&
      data.aal != null &&
      data.aal > 0
        ? newValue / data.aal
        : null;
    applyWithMultipleFloorCheck(derivedMultiple, () =>
      handlePerilOverrideChange(
        rowData.layerId,
        data.editablePerilCode!,
        newValue != null ? { premium: newValue } : undefined
      )
    );
  };

  // Layers-table peril rows → the canonical peril codes PMI is keyed by.
  // named_storm maps to the combined wind+surge peril (PWH_PSH), the code
  // new reports price named storm on.
  const headerColumnGroup = (
    <ColumnGroup>
      <Row>
        <Column header="Layer" rowSpan={2} />
        <Column header="Peril" rowSpan={2} />
        <Column
          header="Active Results (defaults AIR)"
          headerTooltip="Prices each peril on the source picked under Peril Sources. This is the premium quoted and sent to PolicyFly."
          colSpan={showTal ? 10 : 9}
          alignHeader="center"
        />
        <Column
          header="Luminor Blend View"
          headerTooltip="Prices each peril on a fixed source: RMS 25, KatRisk for flood, and AIR for Wind / SCS. Peril Sources does not change it."
          colSpan={showTal ? 8 : 7}
          alignHeader="center"
          headerClassName={styles.lrSectionStart}
        />
        {sourcesExpanded && (
          <Column
            header="Modeled Loss Ratios"
            colSpan={5}
            alignHeader="center"
            headerClassName={styles.lrSectionStart}
          />
        )}
        {sourcesExpanded &&
          sources.map(([source, name]) => (
            <Column
              key={source}
              header={`Modeled - ${name}`}
              colSpan={showTal ? 6 : 5}
            />
          ))}
      </Row>
      <Row>
        {/* Results sub-headers */}
        <Column key="results-source" header="Source" />
        <Column
          key="results-enrichment"
          header="Enrichment"
          headerTooltip="The vendor data the source's live run modeled with. Switch runs with the Vendor data button."
        />
        <Column
          key="results-aal"
          header={lossHeader}
          headerTooltip={lossHeaderTooltip}
        />
        {showTal && (
          <Column
            key="results-annual-aal"
            header="AAL"
            headerTooltip={annualHeaderTooltip}
          />
        )}
        <Column key="results-multiple" header="Multiple" />
        <Column key="results-air-loss-ratio" header="AIR LR" />
        <Column key="results-loss-ratio" header="LR" />
        <Column key="results-layer-premium" header="Layer Prem" />
        <Column key="results-rate" header="Rate" />
        <Column key="results-share-premium" header="Share Prem" />
        {/* Luminor Blend View sub-headers */}
        <Column
          key="blend-source"
          header="Source"
          headerClassName={styles.lrSectionStart}
        />
        <Column
          key="blend-aal"
          header={lossHeader}
          headerTooltip={lossHeaderTooltip}
        />
        {showTal && (
          <Column
            key="blend-annual-aal"
            header="AAL"
            headerTooltip={annualHeaderTooltip}
          />
        )}
        <Column key="blend-multiple" header="Multiple" />
        <Column key="blend-loss-ratio" header="LR" />
        <Column key="blend-layer-premium" header="Layer Prem" />
        <Column key="blend-rate" header="Rate" />
        <Column key="blend-share-premium" header="Share Prem" />
        {/* Loss-ratio comparison sub-headers */}
        {sourcesExpanded &&
          LR_MODEL_COLUMNS.map(({ source, header }, i) => (
            <Column
              key={`lr-${source}`}
              header={header}
              alignHeader="center"
              headerClassName={i === 0 ? styles.lrSectionStart : undefined}
            />
          ))}
        {/* Provider sub-headers (when expanded) */}
        {sourcesExpanded &&
          sources.flatMap(([source]) => [
            <Column
              key={`${source}-aal`}
              header={lossHeader}
              headerTooltip={lossHeaderTooltip}
            />,
            ...(showTal
              ? [
                  <Column
                    key={`${source}-annual-aal`}
                    header="AAL"
                    headerTooltip={annualHeaderTooltip}
                  />,
                ]
              : []),
            <Column key={`${source}-multiple`} header="Multiple" />,
            <Column key={`${source}-layer-premium`} header="Layer Prem" />,
            <Column key={`${source}-rate`} header="Rate" />,
            <Column key={`${source}-share-premium`} header="Share Prem" />,
          ])}
      </Row>
    </ColumnGroup>
  );

  return (
    <Card title={title || "Layers"}>
      <div className={styles.container}>
        <IntegrationStatusMessage
          reportId={specialtyPropertyInfo.report_id}
          sources={selectedPerilSources}
        />
        <div className={styles.premiumTable}>
          <div className={styles.premiumTableHeader}>
            <MultiSelect
              value={enabledLayers}
              options={layerOptions}
              onChange={(e) => {
                const newInfo = cloneDeep(
                  specialtyPropertyInfo.report_json.layer_info
                );
                for (const layer of newInfo) {
                  layer.is_enabled = (e.value as string[]).some(
                    (selected) => selected === layer.layer_id
                  );
                }
                updateReport.mutate({ layerInfo: newInfo });
              }}
              placeholder="Select layers"
              style={{ width: "250px" }}
            />
            <Button
              icon="pi pi-plus"
              onClick={openAddDialog}
              tooltip="Add layer"
              text
              raised
              tooltipOptions={{ position: "top" }}
            />
            <Button
              icon={
                sourcesExpanded ? "pi pi-chevron-left" : "pi pi-chevron-right"
              }
              onClick={() => setSourcesExpanded((prev) => !prev)}
              tooltip={
                sourcesExpanded
                  ? "Collapse provider detail"
                  : "Expand provider detail"
              }
              text
              raised
              tooltipOptions={{ position: "top" }}
            />
            {(() => {
              const brokerCommission =
                specialtyPropertyInfo.report_json.policy_info
                  ?.broker_commission_percentage;
              const factor =
                premiumData?.layers?.[0]?.commission_adjustment_factor ?? 1.0;
              const commissionLabel =
                brokerCommission != null
                  ? `${brokerCommission}%`
                  : "17.5% (default)";
              return (
                <TooltipWrapper
                  tooltipText={
                    "Premiums are quoted at a default broker commission of 17.5%. " +
                    "When the actual commission differs, gross premiums are scaled by " +
                    "(1 − 0.175) / (1 − commission) to keep the carrier's net premium constant."
                  }
                >
                  <span className={styles.commissionSummary}>
                    Commission:{" "}
                    <span className={styles.commissionSummaryValue}>
                      {commissionLabel}
                    </span>
                    <span>·</span>
                    Adjustment:{" "}
                    <span className={styles.commissionSummaryValue}>
                      {factor.toFixed(4)}x
                    </span>
                  </span>
                </TooltipWrapper>
              );
            })()}
          </div>
          <div className={styles.perilSourcePicker}>
            <Button
              label="Vendor data"
              icon="pi pi-database"
              size="small"
              outlined
              className={styles.vendorDataButton}
              onClick={() => setShowVendorDataDialog(true)}
              disabled={!vendorEnrichment}
              tooltip="Choose which completed run each vendor prices from: broker data, Hazard Hub & Nearmap or Hazard Hub & Cape."
              tooltipOptions={{ position: "top", showDelay: 300 }}
            />
            <VendorEnrichmentDialog
              reportId={specialtyPropertyInfo.report_id}
              state={vendorEnrichment}
              visible={showVendorDataDialog}
              onHide={() => setShowVendorDataDialog(false)}
            />
            <span className={styles.perilSourceDivider} />
            <span className={styles.perilSourcePickerLabel}>
              Peril Sources:
            </span>
            {(
              specialtyPropertyInfo.report_json.policy_info?.included_perils ??
              []
            )
              .filter((p) => p !== "equipment_breakdown")
              .sort((a, b) => {
                const ai = PERIL_TYPE_ORDER.indexOf(a);
                const bi = PERIL_TYPE_ORDER.indexOf(b);
                return (
                  (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi)
                );
              })
              .map((peril) => {
                const currentSources =
                  specialtyPropertyInfo.report_json.policy_info?.peril_sources;
                const currentSource = currentSources?.[peril] ?? null;
                // Use the backend-resolved source for this peril
                const perilBreakdown =
                  premiumData?.layers?.[0]?.peril_types[peril];
                const backendResolved = perilBreakdown?.resolved.source;
                const displaySource = currentSource ?? backendResolved;
                // Options come from the backend's per-peril source list. The
                // peril's currently-selected source might not be on that list
                // (e.g. one chosen earlier that's since dropped off) — add it
                // too, or the dropdown would render blank instead of showing
                // the current selection.
                const offerable = perilBreakdown?.offerable_sources ?? [];
                const sourceValues =
                  displaySource && !offerable.includes(displaySource)
                    ? [...offerable, displaySource]
                    : offerable;
                const sourceOptions = sourceValues.map((value) => ({
                  label: SOURCE_LABELS[value],
                  value,
                }));
                return (
                  <span key={peril} className={styles.perilSourceItem}>
                    <span className={styles.perilSourceName}>
                      {layersPerilName(peril)}
                    </span>
                    <Dropdown
                      value={displaySource}
                      options={sourceOptions}
                      onChange={(e) => {
                        const policyInfo = cloneDeep(
                          specialtyPropertyInfo.report_json.policy_info
                        );
                        if (!policyInfo) return;
                        policyInfo.peril_sources =
                          policyInfo.peril_sources ?? {};
                        policyInfo.peril_sources[peril] = e.value;
                        // Mark as edited so it survives report reruns
                        const edited = new Set(policyInfo.edited_fields ?? []);
                        edited.add("peril_sources");
                        policyInfo.edited_fields = [...edited];
                        updateReport.mutate({ policyInfo });
                      }}
                      className={styles.perilSourceDropdown}
                      panelClassName={styles.perilSourcePanel}
                    />
                  </span>
                );
              })}
          </div>
          <Dialog
            header={isEditing ? "Edit Layer" : "Add Layer"}
            visible={showLayerDialog}
            onHide={resetLayerDialog}
            style={{ width: "520px" }}
          >
            <div className={styles.addLayerForm}>
              <TabView>
                <TabPanel header="Layer">
                  <div className={styles.addLayerForm}>
                    <label className={styles.addLayerField}>
                      <span>Attachment Point ($)</span>
                      <InputNumber
                        value={newStartingPoint}
                        onChange={(e) => setNewStartingPoint(e.value ?? null)}
                        mode="currency"
                        currency="USD"
                        locale="en-US"
                        maxFractionDigits={0}
                        placeholder="0"
                        disabled={isEditing}
                      />
                    </label>
                    <label className={styles.addLayerField}>
                      <span>Limit ($)</span>
                      <InputNumber
                        value={newRange}
                        onChange={(e) => setNewRange(e.value ?? null)}
                        mode="currency"
                        currency="USD"
                        locale="en-US"
                        maxFractionDigits={0}
                        placeholder="0"
                        disabled={isEditing}
                      />
                    </label>
                    <div className={styles.addLayerField}>
                      <span>Participation</span>
                      <div className="p-inputgroup">
                        <InputNumber
                          value={newParticipation}
                          onChange={(e) => setNewParticipation(e.value ?? null)}
                          mode="currency"
                          currency="USD"
                          locale="en-US"
                          maxFractionDigits={0}
                          placeholder="Same as limit"
                          style={{ flex: 3 }}
                        />
                        <InputNumber
                          value={
                            newParticipation != null && newRange
                              ? (newParticipation / newRange) * 100
                              : null
                          }
                          onChange={(e) => {
                            if (e.value != null && newRange) {
                              setNewParticipation(
                                Math.round((e.value / 100) * newRange)
                              );
                            } else {
                              setNewParticipation(null);
                            }
                          }}
                          suffix="%"
                          maxFractionDigits={2}
                          placeholder="100%"
                          style={{ flex: 1 }}
                        />
                      </div>
                    </div>
                    <label className={styles.addLayerField}>
                      <span>Target Premium ($)</span>
                      <InputNumber
                        value={newTarget}
                        onChange={(e) => setNewTarget(e.value ?? null)}
                        mode="currency"
                        currency="USD"
                        locale="en-US"
                        maxFractionDigits={0}
                        placeholder="Optional"
                      />
                    </label>
                    {layerPreview && (
                      <div className={styles.layerPreview}>{layerPreview}</div>
                    )}
                    <div className={styles.competitorPricingSection}>
                      <div className={styles.competitorPricingHeader}>
                        <span>Competitor Pricing</span>
                        <Button
                          icon="pi pi-plus"
                          text
                          size="small"
                          tooltip="Add competitor entry"
                          tooltipOptions={{ position: "top" }}
                          onClick={() =>
                            setNewCompetitorPricing((prev) => [
                              ...prev,
                              { carrier_name: "", price: null, notes: null },
                            ])
                          }
                        />
                      </div>
                      {newCompetitorPricing.length === 0 && (
                        <div className={styles.competitorPricingEmpty}>
                          No entries yet.
                        </div>
                      )}
                      {newCompetitorPricing.map((entry, idx) => (
                        <div
                          key={idx}
                          className={styles.competitorPricingEntry}
                        >
                          <div className={styles.competitorPricingTopRow}>
                            <InputText
                              value={entry.carrier_name}
                              onChange={(e) =>
                                setNewCompetitorPricing((prev) =>
                                  prev.map((c, i) =>
                                    i === idx
                                      ? { ...c, carrier_name: e.target.value }
                                      : c
                                  )
                                )
                              }
                              placeholder="Carrier *"
                              className={
                                entry.carrier_name.trim().length === 0
                                  ? "p-invalid"
                                  : ""
                              }
                            />
                            <InputNumber
                              value={entry.price ?? null}
                              onChange={(e) =>
                                setNewCompetitorPricing((prev) =>
                                  prev.map((c, i) =>
                                    i === idx
                                      ? { ...c, price: e.value ?? null }
                                      : c
                                  )
                                )
                              }
                              mode="currency"
                              currency="USD"
                              locale="en-US"
                              maxFractionDigits={0}
                              placeholder="Price"
                            />
                            <Button
                              icon="pi pi-trash"
                              text
                              severity="danger"
                              size="small"
                              onClick={() =>
                                setNewCompetitorPricing((prev) =>
                                  prev.filter((_, i) => i !== idx)
                                )
                              }
                            />
                          </div>
                          <InputTextarea
                            value={entry.notes ?? ""}
                            onChange={(e) =>
                              setNewCompetitorPricing((prev) =>
                                prev.map((c, i) =>
                                  i === idx
                                    ? { ...c, notes: e.target.value || null }
                                    : c
                                )
                              )
                            }
                            placeholder="Notes"
                            rows={2}
                            autoResize
                            className={styles.competitorPricingNotes}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </TabPanel>
                <TabPanel header="Policy Terms">
                  <div className={styles.addLayerForm}>
                    <div className={styles.addLayerField}>
                      <div className={styles.termHeader}>
                        <span>Included Perils</span>
                        {perilsOverridden && (
                          <>
                            <span className={styles.overrideBadge}>
                              Overridden
                            </span>
                            <Button
                              label="Revert"
                              icon="pi pi-replay"
                              text
                              size="small"
                              onClick={() => setNewIncludedPerils(reportPerils)}
                            />
                          </>
                        )}
                      </div>
                      <PerilCheckboxes
                        value={newIncludedPerils}
                        onChange={setNewIncludedPerils}
                      />
                    </div>
                    <div className={styles.addLayerField}>
                      <div className={styles.termHeader}>
                        <span>Policy Type</span>
                        {formOverridden && (
                          <>
                            <span className={styles.overrideBadge}>
                              Overridden
                            </span>
                            <Button
                              label="Revert"
                              icon="pi pi-replay"
                              text
                              size="small"
                              onClick={() => setNewFormSelection(reportForm)}
                            />
                          </>
                        )}
                      </div>
                      <FormSelectionDropdown
                        value={newFormSelection}
                        onChange={setNewFormSelection}
                        hideLabel
                      />
                    </div>
                    <div className={styles.addLayerField}>
                      <div className={styles.termHeader}>
                        <span>Broker Commission (%)</span>
                        {commissionOverridden && (
                          <>
                            <span className={styles.overrideBadge}>
                              Overridden
                            </span>
                            <Button
                              label="Revert"
                              icon="pi pi-replay"
                              text
                              size="small"
                              onClick={() => setNewCommission(reportCommission)}
                            />
                          </>
                        )}
                      </div>
                      <BrokerCommissionInput
                        value={newCommission}
                        onChange={setNewCommission}
                        hideLabel
                      />
                    </div>
                  </div>
                </TabPanel>
              </TabView>
              <Button
                label={isEditing ? "Save" : "Add"}
                icon={isEditing ? "pi pi-check" : "pi pi-plus"}
                onClick={handleSaveLayer}
                disabled={
                  newStartingPoint == null ||
                  newRange == null ||
                  newRange <= 0 ||
                  !competitorPricingValid ||
                  updateReport.isPending
                }
                loading={updateReport.isPending}
                style={{ marginTop: "8px" }}
              />
            </div>
          </Dialog>
          <BulkPerilOverrideDialog
            layer={
              bulkOverrideLayerId
                ? (sortedLayers.find(
                    (L) => L.layer_id === bulkOverrideLayerId
                  ) ?? null)
                : null
            }
            premium={
              bulkOverrideLayerId
                ? premiumByLayer.get(bulkOverrideLayerId)
                : undefined
            }
            onHide={() => setBulkOverrideLayerId(null)}
            isPending={updateReport.isPending}
            onApply={handleBulkPremiumApply}
          />
          <GateExposureDialog
            reportId={specialtyPropertyInfo.report_id}
            layer={
              gateExposureLayerId
                ? (sortedLayers.find(
                    (L) => L.layer_id === gateExposureLayerId
                  ) ?? null)
                : null
            }
            onHide={() => setGateExposureLayerId(null)}
          />
          {pmiEnabled && (
            <PmiDialog
              reportId={specialtyPropertyInfo.report_id}
              layer={
                pmiLayerId
                  ? (sortedLayers.find((L) => L.layer_id === pmiLayerId) ??
                    null)
                  : null
              }
              onHide={() => setPmiLayerId(null)}
            />
          )}
          <FinancialModelAuditDialog
            reportId={specialtyPropertyInfo.report_id}
            layer={
              auditLayerId
                ? (sortedLayers.find((L) => L.layer_id === auditLayerId) ??
                  null)
                : null
            }
            layerBreakdown={
              auditLayerId ? premiumByLayer.get(auditLayerId) : undefined
            }
            onHide={() => setAuditLayerId(null)}
          />
          {firstPremiumLoading ? (
            <div className={styles.loadingContainer}>
              <ProgressSpinner />
            </div>
          ) : premiumError ? (
            <div className={styles.errorContainer}>
              Something went wrong calculating premiums. Let us know which
              report you were working on in #engineering.
            </div>
          ) : (
            <div
              className={`pantheon-small-table ${styles.scrollTopWrapper} ${updateReport.isPending || premiumLoading ? styles.tableStale : ""}`}
            >
              <DataTable<PremiumPerilRow[]>
                value={premiumRows}
                size="small"
                headerColumnGroup={headerColumnGroup}
                rowGroupMode="rowspan"
                groupRowsBy="layerId"
                scrollHeight="70vh"
                tableStyle={{ width: "auto" }}
                rowClassName={(rowData: PremiumPerilRow) => {
                  const classes: string[] = [];
                  if (rowData.rowKind === "total") {
                    classes.push(styles.layerTotalRow);
                  }
                  if (rowData.rowKind === "perilCode") {
                    classes.push(styles.perilCodeRow);
                  }
                  if (rowData.rowKind === "commissionAdj") {
                    classes.push(styles.commissionAdjRow);
                  }
                  if (rowData.isFirstInGroup) {
                    classes.push(styles.layerGroupStart);
                  }
                  return classes.join(" ");
                }}
                editMode="cell"
              >
                <Column
                  field="layerId"
                  style={{ width: "200px" }}
                  bodyClassName={styles.layerCell}
                  body={(rowData: PremiumPerilRow) => {
                    const layer =
                      specialtyPropertyInfo.report_json.layer_info.find(
                        (L) => L.layer_id === rowData.layerId
                      );
                    const overrideCount = layer ? layerOverrideCount(layer) : 0;
                    return (
                      <div>
                        <span className={styles.layerNameRow}>
                          <span>{readableLayer(rowData.layer)}</span>
                          {rowData.isFirstInGroup && overrideCount > 0 && (
                            <span className={styles.overrideBadge}>
                              {overrideCount} override
                              {overrideCount > 1 ? "s" : ""}
                            </span>
                          )}
                        </span>
                        {rowData.isFirstInGroup && layer && (
                          <div className={styles.layerActions}>
                            <Button
                              label="Edit layer"
                              icon="pi pi-pencil"
                              text
                              size="small"
                              className={styles.layerActionButton}
                              onClick={() => openEditDialog(layer)}
                            />
                            <Button
                              label="Set target"
                              icon="pi pi-sliders-h"
                              text
                              size="small"
                              className={styles.layerActionButton}
                              onClick={() =>
                                setBulkOverrideLayerId(rowData.layerId)
                              }
                            />
                            <Button
                              label="Gate exposure"
                              icon="pi pi-chart-bar"
                              text
                              size="small"
                              className={styles.layerActionButton}
                              onClick={() =>
                                setGateExposureLayerId(rowData.layerId)
                              }
                            />
                            <Button
                              label="Model breakdown"
                              icon="pi pi-sitemap"
                              text
                              size="small"
                              className={styles.layerActionButton}
                              onClick={() => setAuditLayerId(rowData.layerId)}
                            />
                            {pmiEnabled && (
                              <Button
                                label="Portfolio impact"
                                icon="pi pi-chart-line"
                                text
                                size="small"
                                className={styles.layerActionButton}
                                onClick={() => setPmiLayerId(rowData.layerId)}
                              />
                            )}
                          </div>
                        )}
                        {rowData.isFirstInGroup && (
                          <>
                            <div className={styles.perilSentence}>
                              {perilSentenceFromEnabledPerils(
                                specialtyPropertyInfo.report_json.policy_info
                                  ?.included_perils ?? [],
                                specialtyPropertyInfo.report_json.policy_info
                                  ?.peril_sentence_override
                              )}
                            </div>
                            <div className={styles.layerMeta}>
                              <span>
                                Part:{" "}
                                {inlineEdit?.layerId === rowData.layerId &&
                                inlineEdit.field === "participation" ? (
                                  <InputNumber
                                    className={styles.inlineEditInput}
                                    value={inlineEdit.value}
                                    onChange={(e) =>
                                      setInlineEdit({
                                        ...inlineEdit,
                                        value: e.value ?? null,
                                      })
                                    }
                                    onBlur={() => saveInlineEdit(rowData.layer)}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        saveInlineEdit(rowData.layer);
                                      } else if (e.key === "Escape") {
                                        closeInlineEdit();
                                      }
                                    }}
                                    suffix="%"
                                    min={0}
                                    max={100}
                                    maxFractionDigits={2}
                                    placeholder="100%"
                                    autoFocus
                                  />
                                ) : (
                                  <span
                                    className={styles.inlineEditValue}
                                    title="Click to edit"
                                    onClick={() =>
                                      openInlineEdit(
                                        rowData.layer,
                                        "participation"
                                      )
                                    }
                                  >
                                    {formatParticipationPercent(
                                      participationPercent(rowData.layer)
                                    )}
                                  </span>
                                )}
                              </span>
                              <span>
                                Target:{" "}
                                {inlineEdit?.layerId === rowData.layerId &&
                                inlineEdit.field === "target" ? (
                                  <InputNumber
                                    className={styles.inlineEditInput}
                                    value={inlineEdit.value}
                                    onChange={(e) =>
                                      setInlineEdit({
                                        ...inlineEdit,
                                        value: e.value ?? null,
                                      })
                                    }
                                    onBlur={() => saveInlineEdit(rowData.layer)}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        saveInlineEdit(rowData.layer);
                                      } else if (e.key === "Escape") {
                                        closeInlineEdit();
                                      }
                                    }}
                                    mode="currency"
                                    currency="USD"
                                    locale="en-US"
                                    maxFractionDigits={0}
                                    autoFocus
                                  />
                                ) : rowData.layer.target != null ? (
                                  <>
                                    <span
                                      className={styles.inlineEditValue}
                                      title="Click to edit"
                                      onClick={() =>
                                        openInlineEdit(rowData.layer, "target")
                                      }
                                    >
                                      {formatExactMoneyDollars(
                                        rowData.layer.target,
                                        {
                                          withRounding: true,
                                          withDollarSign: true,
                                        }
                                      )}
                                    </span>
                                    {tiv ? (
                                      <span
                                        style={{
                                          fontFamily: "monospace",
                                          marginLeft: "4px",
                                        }}
                                      >
                                        (
                                        {(
                                          (rowData.layer.target / tiv) *
                                          100
                                        ).toFixed(2)}
                                        )
                                      </span>
                                    ) : null}
                                  </>
                                ) : (
                                  <span
                                    className={`${styles.inlineEditValue} ${styles.inlineEditNone}`}
                                    title="Click to set"
                                    onClick={() =>
                                      openInlineEdit(rowData.layer, "target")
                                    }
                                  >
                                    None
                                  </span>
                                )}
                              </span>
                            </div>
                            {(rowData.layer.competitor_pricing ?? []).length >
                              0 && (
                              <div className={styles.competitorPricingList}>
                                <div
                                  className={styles.competitorPricingListLabel}
                                >
                                  Competitor Pricing
                                </div>
                                {(rowData.layer.competitor_pricing ?? []).map(
                                  (entry, idx) => (
                                    <div
                                      key={idx}
                                      className={styles.competitorPricingPill}
                                    >
                                      <span
                                        className={
                                          styles.competitorPricingCarrier
                                        }
                                      >
                                        {entry.carrier_name}
                                      </span>
                                      {entry.price != null && (
                                        <span
                                          className={
                                            styles.competitorPricingPrice
                                          }
                                        >
                                          {formatExactMoneyDollars(
                                            entry.price,
                                            {
                                              withRounding: true,
                                              withDollarSign: true,
                                            }
                                          )}
                                        </span>
                                      )}
                                      {entry.notes && (
                                        <TooltipWrapper
                                          tooltipText={entry.notes}
                                        >
                                          <i
                                            className="pi pi-info-circle"
                                            style={{
                                              fontSize: "0.7rem",
                                              opacity: 0.7,
                                              cursor: "help",
                                            }}
                                          />
                                        </TooltipWrapper>
                                      )}
                                    </div>
                                  )
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  }}
                />
                <Column
                  field="peril"
                  style={{ width: "140px" }}
                  body={(rowData: PremiumPerilRow) => {
                    if (rowData.rowKind === "total") {
                      return <span className={styles.totalPeril}>Total</span>;
                    }
                    if (rowData.rowKind === "commissionAdj") {
                      const tooltipText =
                        rowData.commissionPercent != null
                          ? `Gross premium is scaled by (1 − 17.5%) / (1 − ${rowData.commissionPercent}%) so the carrier's net premium is unchanged. AAL is not modified.`
                          : "Gross premium adjustment applied because the broker commission differs from the 17.5% default. AAL is not modified.";
                      return (
                        <TooltipWrapper tooltipText={tooltipText}>
                          <span className={styles.commissionAdjLabel}>
                            Commission Adj.
                          </span>
                        </TooltipWrapper>
                      );
                    }
                    if (rowData.rowKind === "perilCode") {
                      return (
                        <span className={styles.perilCodeLabel}>
                          {perilCodeDisplayName(rowData.perilCode)}
                        </span>
                      );
                    }
                    return layersPerilName(rowData.peril);
                  }}
                />
                {/* ── Active Results columns (always visible) ── */}
                <Column
                  key="results-source"
                  style={{ width: "80px" }}
                  bodyClassName={(rowData: PremiumPerilRow) =>
                    rowData.rowKind === "total" ? styles.resultsTotalCell : ""
                  }
                  body={(rowData: PremiumPerilRow) =>
                    sourceBody(rowData, "resolved")
                  }
                />
                <Column
                  key="results-enrichment"
                  style={{ width: "70px" }}
                  bodyClassName={(rowData: PremiumPerilRow) =>
                    rowData.rowKind === "total" ? styles.resultsTotalCell : ""
                  }
                  body={(rowData: PremiumPerilRow) => {
                    if (rowData.rowKind === "total") return "";
                    if (rowData.rowKind === "commissionAdj") return "";
                    if (!rowData.resolvedSource) return "";
                    // Every vendor behind the row: a peril type's parts can
                    // price from several (AOP from the rater beside PWW from a
                    // CAT vendor), as can a blend. One pill when their live runs
                    // agree, else one per vendor.
                    const sources: DisplayPerilDataSource[] =
                      rowData.rowKind === "perilCode"
                        ? [
                            displayPerilDataSource(
                              rowData.perilCode,
                              rowData.resolvedSource
                            ),
                          ]
                        : rowData.peril === "equipment_breakdown"
                          ? ["eqbd"]
                          : rowData.partSources;
                    const vendors = Array.from(
                      new Set(
                        sources.flatMap((s) =>
                          enrichmentVendorsForSource(s, rowData.peril)
                        )
                      )
                    );
                    const live = vendors.flatMap((vendor) => {
                      const source = liveSourceByVendor.get(vendor);
                      return source === undefined ? [] : [{ vendor, source }];
                    });
                    if (live.length === 0) return "";
                    const distinct = new Set(live.map((l) => l.source));
                    return (
                      <span className={styles.enrichmentCell}>
                        {(distinct.size === 1 ? live.slice(0, 1) : live).map(
                          ({ vendor, source }) => (
                            <EnrichmentBadge
                              key={vendor}
                              source={source}
                              title={`${ENRICHMENT_VENDOR_LABELS[vendor]} prices from its ${
                                source === "cape"
                                  ? "Hazard Hub & Cape"
                                  : source === "nearmap"
                                    ? "Hazard Hub & Nearmap"
                                    : "broker data"
                              } run`}
                            />
                          )
                        )}
                      </span>
                    );
                  }}
                />
                <Column
                  key="results-aal"
                  style={{ width: "85px" }}
                  bodyClassName={(rowData: PremiumPerilRow) =>
                    rowData.rowKind === "total" ? styles.resultsTotalCell : ""
                  }
                  body={(rowData: PremiumPerilRow) =>
                    withCatMarker(
                      rowData,
                      aalBody(lossView(rowData, "resolved"))
                    )
                  }
                />
                {showTal && (
                  <Column
                    key="results-annual-aal"
                    style={{ width: "85px" }}
                    bodyClassName={(rowData: PremiumPerilRow) =>
                      rowData.rowKind === "total" ? styles.resultsTotalCell : ""
                    }
                    body={(rowData: PremiumPerilRow) =>
                      withCatMarker(
                        rowData,
                        annualAalBody(lossView(rowData, "resolved"))
                      )
                    }
                  />
                )}
                <Column
                  key="results-multiple"
                  style={{ width: "65px" }}
                  bodyClassName={(rowData: PremiumPerilRow) =>
                    rowData.rowKind === "total" ? styles.resultsTotalCell : ""
                  }
                  onCellEditComplete={(options) =>
                    multipleEditComplete(options, (r) => r.resolved)
                  }
                  onBeforeCellEditShow={(options) =>
                    isMultipleEditable(options.rowData.resolved)
                  }
                  editor={(options) => (
                    <InputNumber
                      value={options.rowData.resolved.multiple}
                      onValueChange={(e) => options.editorCallback?.(e.value)}
                      minFractionDigits={1}
                      className={styles.overrideInput}
                      placeholder="-"
                      disabled={updateReport.isPending}
                    />
                  )}
                  body={(rowData: PremiumPerilRow) => {
                    if (rowData.rowKind === "commissionAdj") {
                      return (
                        <span className={styles.commissionAdjFactor}>
                          {rowData.factor.toFixed(4)}×
                        </span>
                      );
                    }
                    return withCatMarker(
                      rowData,
                      multipleBody(rowData, lossView(rowData, "resolved"))
                    );
                  }}
                />
                <Column
                  key="results-air-loss-ratio"
                  style={{ width: "60px" }}
                  bodyClassName={(rowData: PremiumPerilRow) =>
                    rowData.rowKind === "total" ? styles.resultsTotalCell : ""
                  }
                  body={(rowData: PremiumPerilRow) =>
                    sourceLrBody("air", rowData)
                  }
                />
                <Column
                  key="results-loss-ratio"
                  style={{ width: "60px" }}
                  bodyClassName={(rowData: PremiumPerilRow) =>
                    rowData.rowKind === "total" ? styles.resultsTotalCell : ""
                  }
                  body={(rowData: PremiumPerilRow) =>
                    withCatMarker(rowData, lossRatioBody(rowData, "resolved"))
                  }
                />
                <Column
                  key="results-layer-premium"
                  style={{ width: "95px" }}
                  bodyClassName={(rowData: PremiumPerilRow) =>
                    rowData.rowKind === "total" ? styles.resultsTotalCell : ""
                  }
                  onCellEditComplete={(options) =>
                    layerPremEditComplete(options, (r) => r.resolved)
                  }
                  onBeforeCellEditShow={(options) =>
                    isLayerPremEditable(options.rowData.resolved)
                  }
                  editor={(options) => (
                    <InputNumber
                      value={options.rowData.resolved.layerPremium}
                      onValueChange={(e) => options.editorCallback?.(e.value)}
                      mode="currency"
                      currency="USD"
                      locale="en-US"
                      maxFractionDigits={0}
                      className={styles.overrideInput}
                      placeholder="-"
                      disabled={updateReport.isPending}
                    />
                  )}
                  body={(rowData: PremiumPerilRow) =>
                    layerPremBody(rowData, rowData.resolved)
                  }
                />
                <Column
                  key="results-rate"
                  style={{ width: "50px" }}
                  bodyClassName={(rowData: PremiumPerilRow) =>
                    rowData.rowKind === "total" ? styles.resultsTotalCell : ""
                  }
                  body={(rowData: PremiumPerilRow) => {
                    const data = rowData.resolved;
                    return data.stale || data.layerPremium === null
                      ? "-"
                      : rateBody(data.layerPremium);
                  }}
                />
                <Column
                  key="results-share-premium"
                  style={{ width: "85px" }}
                  bodyClassName={(rowData: PremiumPerilRow) =>
                    rowData.rowKind === "total" ? styles.resultsTotalCell : ""
                  }
                  body={(rowData: PremiumPerilRow) =>
                    sharePremBody(rowData.resolved)
                  }
                />
                {/* ── Luminor Blend View columns (always visible) ── */}
                <Column
                  key="blend-source"
                  style={{ width: "80px" }}
                  bodyClassName={(rowData: PremiumPerilRow) =>
                    `${styles.lrSectionStart} ${
                      rowData.rowKind === "total" ? styles.resultsTotalCell : ""
                    }`
                  }
                  body={(rowData: PremiumPerilRow) =>
                    sourceBody(rowData, "luminorBlend")
                  }
                />
                <Column
                  key="blend-aal"
                  style={{ width: "85px" }}
                  bodyClassName={(rowData: PremiumPerilRow) =>
                    rowData.rowKind === "total" ? styles.resultsTotalCell : ""
                  }
                  body={(rowData: PremiumPerilRow) =>
                    withCatMarker(
                      rowData,
                      aalBody(lossView(rowData, "luminorBlend"))
                    )
                  }
                />
                {showTal && (
                  <Column
                    key="blend-annual-aal"
                    style={{ width: "85px" }}
                    bodyClassName={(rowData: PremiumPerilRow) =>
                      rowData.rowKind === "total" ? styles.resultsTotalCell : ""
                    }
                    body={(rowData: PremiumPerilRow) =>
                      withCatMarker(
                        rowData,
                        annualAalBody(lossView(rowData, "luminorBlend"))
                      )
                    }
                  />
                )}
                <Column
                  key="blend-multiple"
                  style={{ width: "65px" }}
                  bodyClassName={(rowData: PremiumPerilRow) =>
                    rowData.rowKind === "total" ? styles.resultsTotalCell : ""
                  }
                  onCellEditComplete={(options) =>
                    multipleEditComplete(options, (r) => r.luminorBlend)
                  }
                  onBeforeCellEditShow={(options) =>
                    isMultipleEditable(options.rowData.luminorBlend)
                  }
                  editor={(options) => (
                    <InputNumber
                      value={options.rowData.luminorBlend.multiple}
                      onValueChange={(e) => options.editorCallback?.(e.value)}
                      minFractionDigits={1}
                      className={styles.overrideInput}
                      placeholder="-"
                      disabled={updateReport.isPending}
                    />
                  )}
                  body={(rowData: PremiumPerilRow) => {
                    if (rowData.rowKind === "commissionAdj") {
                      return (
                        <span className={styles.commissionAdjFactor}>
                          {rowData.factor.toFixed(4)}×
                        </span>
                      );
                    }
                    return withCatMarker(
                      rowData,
                      multipleBody(rowData, lossView(rowData, "luminorBlend"))
                    );
                  }}
                />
                <Column
                  key="blend-loss-ratio"
                  style={{ width: "60px" }}
                  bodyClassName={(rowData: PremiumPerilRow) =>
                    rowData.rowKind === "total" ? styles.resultsTotalCell : ""
                  }
                  body={(rowData: PremiumPerilRow) =>
                    withCatMarker(
                      rowData,
                      lossRatioBody(rowData, "luminorBlend")
                    )
                  }
                />
                <Column
                  key="blend-layer-premium"
                  style={{ width: "95px" }}
                  bodyClassName={(rowData: PremiumPerilRow) =>
                    rowData.rowKind === "total" ? styles.resultsTotalCell : ""
                  }
                  onCellEditComplete={(options) =>
                    layerPremEditComplete(options, (r) => r.luminorBlend)
                  }
                  onBeforeCellEditShow={(options) =>
                    isLayerPremEditable(options.rowData.luminorBlend)
                  }
                  editor={(options) => (
                    <InputNumber
                      value={options.rowData.luminorBlend.layerPremium}
                      onValueChange={(e) => options.editorCallback?.(e.value)}
                      mode="currency"
                      currency="USD"
                      locale="en-US"
                      maxFractionDigits={0}
                      className={styles.overrideInput}
                      placeholder="-"
                      disabled={updateReport.isPending}
                    />
                  )}
                  body={(rowData: PremiumPerilRow) =>
                    layerPremBody(rowData, rowData.luminorBlend)
                  }
                />
                <Column
                  key="blend-rate"
                  style={{ width: "50px" }}
                  bodyClassName={(rowData: PremiumPerilRow) =>
                    rowData.rowKind === "total" ? styles.resultsTotalCell : ""
                  }
                  body={(rowData: PremiumPerilRow) => {
                    const data = rowData.luminorBlend;
                    return data.stale || data.layerPremium === null
                      ? "-"
                      : rateBody(data.layerPremium);
                  }}
                />
                <Column
                  key="blend-share-premium"
                  style={{ width: "85px" }}
                  bodyClassName={(rowData: PremiumPerilRow) =>
                    rowData.rowKind === "total" ? styles.resultsTotalCell : ""
                  }
                  body={(rowData: PremiumPerilRow) =>
                    sharePremBody(rowData.luminorBlend)
                  }
                />
                {/* ── Loss-ratio comparison columns (expanded only) ── */}
                {sourcesExpanded &&
                  LR_MODEL_COLUMNS.map(({ source }, i) => (
                    <Column
                      key={`lr-${source}`}
                      style={{ width: "60px" }}
                      bodyClassName={(rowData: PremiumPerilRow) =>
                        `${i === 0 ? `${styles.lrSectionStart} ` : ""}${
                          styles.centerCell
                        } ${
                          rowData.rowKind === "total"
                            ? styles.resultsTotalCell
                            : ""
                        }`
                      }
                      body={(rowData: PremiumPerilRow) =>
                        sourceLrBody(source, rowData)
                      }
                    />
                  ))}
                {/* ── Provider detail columns (expanded only) ── */}
                {sourcesExpanded &&
                  sources.flatMap(([source]) => {
                    const mutedCls = (rowData: PremiumPerilRow) =>
                      isMuted(source, rowData) ? styles.mutedColumn : "";
                    const getData = (r: PremiumPerilRow) => r[source];
                    return [
                      <Column
                        key={`${source}-aal`}
                        style={{ width: "85px" }}
                        bodyClassName={mutedCls}
                        body={(rowData: PremiumPerilRow) =>
                          aalBody(getData(rowData))
                        }
                      />,
                      ...(showTal
                        ? [
                            <Column
                              key={`${source}-annual-aal`}
                              style={{ width: "85px" }}
                              bodyClassName={mutedCls}
                              body={(rowData: PremiumPerilRow) =>
                                annualAalBody(getData(rowData))
                              }
                            />,
                          ]
                        : []),
                      <Column
                        key={`${source}-multiple`}
                        style={{ width: "65px" }}
                        bodyClassName={mutedCls}
                        onCellEditComplete={(options) =>
                          multipleEditComplete(options, getData)
                        }
                        onBeforeCellEditShow={(options) =>
                          isMultipleEditable(getData(options.rowData))
                        }
                        editor={(options) => (
                          <InputNumber
                            value={getData(options.rowData).multiple}
                            onValueChange={(e) =>
                              options.editorCallback?.(e.value)
                            }
                            minFractionDigits={1}
                            className={styles.overrideInput}
                            placeholder="-"
                            disabled={updateReport.isPending}
                          />
                        )}
                        body={(rowData: PremiumPerilRow) =>
                          multipleBody(rowData, getData(rowData))
                        }
                      />,
                      <Column
                        key={`${source}-layer-premium`}
                        style={{ width: "95px" }}
                        bodyClassName={mutedCls}
                        onCellEditComplete={(options) =>
                          layerPremEditComplete(options, getData)
                        }
                        onBeforeCellEditShow={(options) =>
                          isLayerPremEditable(getData(options.rowData))
                        }
                        editor={(options) => (
                          <InputNumber
                            value={getData(options.rowData).layerPremium}
                            onValueChange={(e) =>
                              options.editorCallback?.(e.value)
                            }
                            mode="currency"
                            currency="USD"
                            locale="en-US"
                            maxFractionDigits={0}
                            className={styles.overrideInput}
                            placeholder="-"
                            disabled={updateReport.isPending}
                          />
                        )}
                        body={(rowData: PremiumPerilRow) =>
                          layerPremBody(rowData, getData(rowData))
                        }
                      />,
                      <Column
                        key={`${source}-rate`}
                        style={{ width: "50px" }}
                        bodyClassName={mutedCls}
                        body={(rowData: PremiumPerilRow) => {
                          const data = getData(rowData);
                          return data.stale || data.layerPremium === null
                            ? "-"
                            : rateBody(data.layerPremium);
                        }}
                      />,
                      <Column
                        key={`${source}-share-premium`}
                        style={{ width: "85px" }}
                        bodyClassName={mutedCls}
                        body={(rowData: PremiumPerilRow) =>
                          sharePremBody(getData(rowData))
                        }
                      />,
                    ];
                  })}
              </DataTable>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};
